import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, CaretDown, ArrowUpRight } from "@phosphor-icons/react";
import { useLanguage } from "../i18n";

const externalLinks = [
  { label: "Behance — Portfolio", href: "https://www.behance.net/lucapedano" },
  { label: "lucapedano.altervista.org", href: "https://lucapedano.altervista.org/" },
];

const links = [
  { key: "intro", href: "#intro" },
  { key: "about", href: "#about" },
  { key: "services", href: "#services" },
  { key: "booking", href: "#booking" },
  { key: "contact", href: "#contact" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <span className="font-display font-semibold tracking-tight text-base hidden sm:block">Luca Pedano</span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-brand-cyan transition-colors duration-300"
            >
              {t.nav[l.key]}
            </a>
          ))}
          <div className="relative">
            <button
              onClick={() => setLinksOpen(!linksOpen)}
              data-testid="nav-links-dropdown-button"
              className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-brand-cyan transition-colors duration-300"
            >
              Link
              <CaretDown size={12} weight="bold" className={`transition-transform duration-300 ${linksOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {linksOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.25 }}
                  className="absolute right-0 top-full mt-3 w-64 rounded-lg border border-white/10 bg-ink/95 backdrop-blur-xl p-2 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)]"
                  data-testid="nav-links-dropdown"
                >
                  <p className="px-4 pt-3 pb-2 font-mono text-[11px] text-white/35 leading-relaxed border-b border-white/5 mb-1" data-testid="nav-links-intro">
                    {t.linksIntro}
                  </p>
                  {externalLinks.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setLinksOpen(false)}
                      data-testid={`nav-external-${l.label.split(" ")[0].toLowerCase()}`}
                      className="flex items-center justify-between gap-3 rounded-md px-4 py-3 font-mono text-xs text-white/60 hover:bg-white/5 hover:text-brand-cyan transition-colors duration-300"
                    >
                      {l.label}
                      <ArrowUpRight size={14} weight="bold" />
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-white/10 p-0.5" data-testid="language-toggle">
            {["it", "en", "fr"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}-button`}
                aria-label={`Switch language to ${l.toUpperCase()}`}
                className={`px-3 py-1 rounded-full font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                  lang === l ? "bg-brand-cyan/15 text-brand-cyan" : "text-white/40 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="#contact"
            data-testid="nav-cta-button"
            className="hidden md:inline-flex items-center rounded-full bg-white text-ink px-5 py-2 font-mono text-xs uppercase tracking-widest hover:bg-brand-cyan transition-colors duration-300"
          >
            {t.navCta}
          </a>
          <button
            className="md:hidden text-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-ink"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.key}`}
                  className="font-mono text-sm uppercase tracking-[0.2em] text-white/70"
                >
                  {t.nav[l.key]}
                </a>
              ))}
              {externalLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-external-${l.label.split(" ")[0].toLowerCase()}`}
                  className="flex items-center gap-2 font-mono text-sm uppercase tracking-[0.2em] text-brand-cyan/70"
                >
                  {l.label}
                  <ArrowUpRight size={14} weight="bold" />
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
